import { useState, useEffect } from 'react';
import useLatestCallback from '@/hooks/useLatestCallback';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { ChevronLeft, MapPin, Phone, Clock, CheckCircle, Package, Truck, Store, Star, Loader2, MessageCircle, Download, Mail, FileText, Smartphone, Car } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Skeleton } from '@/components/ui/skeleton';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import { toast } from 'sonner';
import axios from 'axios';
import { useAuthStore } from '@/store';
import OrderChat from '@/components/chat/OrderChat';
import DriverChat from '@/components/chat/DriverChat';

import { API } from '@/config/api';

const statusSteps = [
  { key: 'pending', label: 'Order Placed', icon: Clock },
  { key: 'confirmed', label: 'Confirmed', icon: CheckCircle },
  { key: 'preparing', label: 'Preparing', icon: Package },
  { key: 'ready', label: 'Ready', icon: Store },
  { key: 'out_for_delivery', label: 'Out for Delivery', icon: Truck },
  { key: 'delivered', label: 'Delivered', icon: CheckCircle }
];

const pickupSteps = [
  { key: 'pending', label: 'Order Placed', icon: Clock },
  { key: 'confirmed', label: 'Confirmed', icon: CheckCircle },
  { key: 'preparing', label: 'Preparing', icon: Package },
  { key: 'ready', label: 'Ready for Pickup', icon: Store },
  { key: 'completed', label: 'Completed', icon: CheckCircle }
];

export default function OrderDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { token, user } = useAuthStore();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [reviewOpen, setReviewOpen] = useState(false);
  const [rating, setRating] = useState(5);
  const [reviewComment, setReviewComment] = useState('');
  const [showChat, setShowChat] = useState(false);
  const [submittingReview, setSubmittingReview] = useState(false);
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [emailAddress, setEmailAddress] = useState('');
  const [sendingEmail, setSendingEmail] = useState(false);
  const [downloadingReceipt, setDownloadingReceipt] = useState(false);
  const [smsDialogOpen, setSmsDialogOpen] = useState(false);
  const [phoneNumber, setPhoneNumber] = useState('');
  const [sendingSms, setSendingSms] = useState(false);
  const [showDriverChat, setShowDriverChat] = useState(false);

  // Runs per order id; the query string is read at that moment, not watched.
  const loadOrder = useLatestCallback(() => {
    fetchOrder();
    
    // Check for payment success
    const sessionId = searchParams.get('session_id');
    if (sessionId) {
      // Payment completed, poll for status
      pollPaymentStatus(sessionId);
    }
  });
  useEffect(() => {
    loadOrder();
  }, [id, loadOrder]);

  const fetchOrder = useLatestCallback(async () => {
    try {
      const response = await axios.get(`${API}/orders/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setOrder(response.data);
    } catch (error) {
      toast.error('Failed to load order');
      navigate('/orders');
    } finally {
      setLoading(false);
    }
  });

  const pollPaymentStatus = useLatestCallback(async (sessionId) => {
    try {
      const response = await axios.get(`${API}/payments/status/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.payment_status === 'paid') {
        toast.success('Payment confirmed! Your order is being prepared.');
        fetchOrder();
      }
    } catch (error) {
      console.error('Failed to poll payment status:', error);
    }
  });

  const handleSubmitReview = async () => {
    if (!rating) {
      toast.error('Please select a rating');
      return;
    }

    setSubmittingReview(true);
    try {
      await axios.post(
        `${API}/reviews`,
        {
          restaurant_id: order.restaurant_id,
          order_id: order.id,
          rating,
          comment: reviewComment || null
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Review submitted! Thank you for your feedback.');
      setReviewOpen(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit review');
    } finally {
      setSubmittingReview(false);
    }
  };

  const handleDownloadReceipt = async () => {
    setDownloadingReceipt(true);
    try {
      const response = await axios.get(`${API}/orders/${id}/receipt/download`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Chain29_Receipt_Order_${id}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Receipt downloaded!');
    } catch (error) {
      toast.error('Failed to download receipt');
    } finally {
      setDownloadingReceipt(false);
    }
  };

  const handleEmailReceipt = async () => {
    setSendingEmail(true);
    try {
      const response = await axios.post(
        `${API}/orders/${id}/receipt/email`,
        { email: emailAddress || null },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(response.data.message);
      setEmailDialogOpen(false);
      setEmailAddress('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send receipt');
    } finally {
      setSendingEmail(false);
    }
  };

  const handlePreviewReceipt = () => {
    window.open(`${API}/orders/${id}/receipt/preview`, '_blank');
  };

  const handleSmsReceipt = async () => {
    setSendingSms(true);
    try {
      const response = await axios.post(
        `${API}/orders/${id}/receipt/sms`,
        { phone: phoneNumber || null },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(response.data.message);
      setSmsDialogOpen(false);
      setPhoneNumber('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send SMS receipt');
    } finally {
      setSendingSms(false);
    }
  };

  const getCurrentStepIndex = () => {
    const steps = order?.order_type === 'pickup' ? pickupSteps : statusSteps;
    const index = steps.findIndex(s => s.key === order?.status);
    return index >= 0 ? index : 0;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-muted/30 py-8">
        <div className="max-w-4xl mx-auto px-4">
          <Skeleton className="h-8 w-1/3 mb-6" />
          <Skeleton className="h-48 w-full mb-6" />
          <Skeleton className="h-64 w-full" />
        </div>
      </div>
    );
  }

  const steps = order?.order_type === 'pickup' ? pickupSteps : statusSteps;
  const currentStepIndex = getCurrentStepIndex();

  return (
    <div className="min-h-screen bg-muted/30 py-8" data-testid="order-detail-page">
      <div className="max-w-4xl mx-auto px-4">
        {/* Header */}
        <div className="flex items-center gap-4 mb-6">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate('/orders')}
            className="rounded-full"
            data-testid="back-button"
          >
            <ChevronLeft className="h-5 w-5" />
          </Button>
          <div className="flex-1">
            <h1 className="text-2xl font-bold">Order #{order?.id}</h1>
            <p className="text-muted-foreground">
              {new Date(order?.created_at).toLocaleString()}
            </p>
          </div>
          
          {/* Receipt Actions */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" className="gap-2">
                <FileText className="h-4 w-4" />
                Receipt
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={handleDownloadReceipt} disabled={downloadingReceipt}>
                <Download className="h-4 w-4 mr-2" />
                {downloadingReceipt ? 'Downloading...' : 'Download PDF'}
              </DropdownMenuItem>
              <DropdownMenuItem onClick={handlePreviewReceipt}>
                <FileText className="h-4 w-4 mr-2" />
                Preview Receipt
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => setEmailDialogOpen(true)}>
                <Mail className="h-4 w-4 mr-2" />
                Email Receipt
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => setSmsDialogOpen(true)}>
                <Smartphone className="h-4 w-4 mr-2" />
                SMS Receipt
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          
          <Badge 
            variant={order?.payment_status === 'paid' ? 'default' : 'secondary'}
            className="capitalize"
          >
            {order?.payment_status}
          </Badge>

        </div>


        {/* Email Receipt Dialog */}
        <Dialog open={emailDialogOpen} onOpenChange={setEmailDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Email Receipt</DialogTitle>
              <DialogDescription>
                Send a copy of your receipt to your email address.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="email">Email Address</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="Enter email (leave blank to use account email)"
                  value={emailAddress}
                  onChange={(e) => setEmailAddress(e.target.value)}
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setEmailDialogOpen(false)}>
                Cancel
              </Button>
              <Button onClick={handleEmailReceipt} disabled={sendingEmail}>
                {sendingEmail ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    Sending...
                  </>
                ) : (
                  <>
                    <Mail className="h-4 w-4 mr-2" />
                    Send Receipt
                  </>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* SMS Receipt Dialog */}
        <Dialog open={smsDialogOpen} onOpenChange={setSmsDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>SMS Receipt</DialogTitle>
              <DialogDescription>
                Send a receipt summary to your phone via SMS.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="phone">Phone Number</Label>
                <Input
                  id="phone"
                  type="tel"
                  placeholder="Enter phone (leave blank to use account phone)"
                  value={phoneNumber}
                  onChange={(e) => setPhoneNumber(e.target.value)}
                />
                <p className="text-xs text-muted-foreground">
                  US format: +1 (555) 123-4567
                </p>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setSmsDialogOpen(false)}>
                Cancel
              </Button>
              <Button onClick={handleSmsReceipt} disabled={sendingSms}>
                {sendingSms ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    Sending...
                  </>
                ) : (
                  <>
                    <Smartphone className="h-4 w-4 mr-2" />
                    Send SMS
                  </>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Order Progress */}
          <div className="lg:col-span-2 space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Order Status</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="relative">
                  {steps.map((step, index) => {
                    const StepIcon = step.icon;
                    const isCompleted = index <= currentStepIndex;
                    const isCurrent = index === currentStepIndex;
                    
                    return (
                      <div key={step.key} className="flex items-start gap-4 mb-6 last:mb-0">
                        <div className={`
                          w-10 h-10 rounded-full flex items-center justify-center shrink-0
                          ${isCompleted 
                            ? 'bg-primary text-primary-foreground' 
                            : 'bg-muted text-muted-foreground'
                          }
                          ${isCurrent ? 'ring-4 ring-primary/20' : ''}
                        `}>
                          <StepIcon className="h-5 w-5" />
                        </div>
                        <div className="flex-1 pt-2">
                          <p className={`font-medium ${isCompleted ? '' : 'text-muted-foreground'}`}>
                            {step.label}
                          </p>
                          {isCurrent && order?.status !== 'cancelled' && (
                            <p className="text-sm text-primary">In progress...</p>
                          )}
                        </div>
                        {index < steps.length - 1 && (
                          <div className={`
                            absolute left-5 w-0.5 h-6 mt-10
                            ${index < currentStepIndex ? 'bg-primary' : 'bg-muted'}
                          `} style={{ marginLeft: '-0.5px', marginTop: `${48 + index * 72}px` }} />
                        )}
                      </div>
                    );
                  })}
                </div>

                {order?.status === 'cancelled' && (
                  <div className="mt-4 p-4 bg-destructive/10 rounded-lg">
                    <p className="text-destructive font-medium">Order Cancelled</p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Order Items */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Order Items</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {order?.items.map((item) => (
                    <div key={item.id} className="flex justify-between">
                      <div>
                        <p className="font-medium">{item.quantity}x {item.name}</p>
                        {item.special_instructions && (
                          <p className="text-sm text-muted-foreground">
                            Note: {item.special_instructions}
                          </p>
                        )}
                      </div>
                      <p className="font-medium">${item.total_price.toFixed(2)}</p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Review Button */}
            {['delivered', 'completed'].includes(order?.status) && (
              <Dialog open={reviewOpen} onOpenChange={setReviewOpen}>
                <DialogTrigger asChild>
                  <Button className="w-full btn-pill" data-testid="leave-review-btn">
                    <Star className="mr-2 h-4 w-4" />
                    Leave a Review
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Rate your order</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4">
                    <div className="flex justify-center gap-2">
                      {[1, 2, 3, 4, 5].map((star) => (
                        <button
                          key={star}
                          onClick={() => setRating(star)}
                          className="p-1"
                          data-testid={`star-${star}`}
                        >
                          <Star 
                            className={`h-8 w-8 ${
                              star <= rating 
                                ? 'fill-amber-400 text-amber-400' 
                                : 'text-muted-foreground/30'
                            }`}
                          />
                        </button>
                      ))}
                    </div>
                    <Textarea
                      value={reviewComment}
                      onChange={(e) => setReviewComment(e.target.value)}
                      placeholder="Share your experience (optional)"
                      className="resize-none"
                      data-testid="review-comment"
                    />
                    <Button
                      onClick={handleSubmitReview}
                      disabled={submittingReview}
                      className="w-full btn-pill"
                      data-testid="submit-review-btn"
                    >
                      {submittingReview ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          Submitting...
                        </>
                      ) : (
                        'Submit Review'
                      )}
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>
            )}
          </div>

          {/* Order Summary */}
          <div className="lg:col-span-1">
            <Card className="sticky top-24">
              <CardHeader>
                <CardTitle className="text-lg">{order?.restaurant_name}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  {order?.order_type === 'delivery' ? (
                    <Truck className="h-4 w-4" />
                  ) : (
                    <Store className="h-4 w-4" />
                  )}
                  <span className="capitalize">{order?.order_type}</span>
                </div>

                {order?.delivery_address && (
                  <div className="flex items-start gap-2 text-sm">
                    <MapPin className="h-4 w-4 mt-0.5 text-muted-foreground" />
                    <span>{order.delivery_address}</span>
                  </div>
                )}

                {order?.special_instructions && (
                  <div className="text-sm">
                    <p className="text-muted-foreground">Special Instructions:</p>
                    <p>{order.special_instructions}</p>
                  </div>
                )}

                <Separator />

                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Subtotal</span>
                    <span>${order?.subtotal.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Tax</span>
                    <span>${order?.tax.toFixed(2)}</span>
                  </div>
                  {order?.delivery_fee > 0 && (
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Delivery Fee</span>
                      <span>${order?.delivery_fee.toFixed(2)}</span>
                    </div>
                  )}
                  {order?.tip > 0 && (
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Tip</span>
                      <span>${order?.tip.toFixed(2)}</span>
                    </div>
                  )}
                  {order?.discount > 0 && (
                    <div className="flex justify-between text-green-600">
                      <span>Discount</span>
                      <span>-${order?.discount.toFixed(2)}</span>
                    </div>
                  )}
                </div>

                <Separator />

                <div className="flex justify-between font-bold text-lg">
                  <span>Total</span>
                  <span>${order?.total.toFixed(2)}</span>
                </div>
              </CardContent>
            </Card>

            {/* Chat with Support Card */}
            {order && !['delivered', 'completed', 'cancelled'].includes(order.status) && (
              <Card className="mt-4">
                <CardContent className="py-4 space-y-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium">Need Help?</p>
                      <p className="text-sm text-muted-foreground">
                        Chat with the restaurant
                      </p>
                    </div>
                    <Button onClick={() => setShowChat(true)} variant="outline" className="gap-2">
                      <MessageCircle className="h-4 w-4" />
                      Restaurant
                    </Button>
                  </div>
                  
                  {/* Driver Chat - only show for delivery orders that are out for delivery */}
                  {order?.order_type === 'delivery' && ['out_for_delivery', 'ready'].includes(order.status) && (
                    <div className="flex items-center justify-between pt-2 border-t">
                      <div>
                        <p className="font-medium">Track Driver</p>
                        <p className="text-sm text-muted-foreground">
                          Contact your driver
                        </p>
                      </div>
                      <Button onClick={() => setShowDriverChat(true)} variant="outline" className="gap-2">
                        <Car className="h-4 w-4" />
                        Driver
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </div>

      {/* Restaurant Chat Component */}
      {showChat && order && (
        <OrderChat 
          orderId={parseInt(id)} 
          onClose={() => setShowChat(false)}
        />
      )}

      {/* Driver Chat Component */}
      {showDriverChat && order && (
        <DriverChat 
          orderId={parseInt(id)} 
          onClose={() => setShowDriverChat(false)}
        />
      )}
    </div>
  );
}
